import React, { useMemo } from 'react';
import './OrderCardBubbles.css';

const BUBBLE_COUNT = 48;
const SIZE_MIN = 3;
const SIZE_MAX = 6;
const CARD_HEIGHT_PX = 450;

const RARITY_BUBBLE_COLORS = {
  common: {
    inner: 'rgba(186, 186, 192, 0.95)',
    mid: 'rgba(128, 128, 134, 0.9)',
    outer: 'rgba(82, 82, 88, 0.7)',
    shadow1: 'rgba(150, 150, 156, 0.85)',
    shadow2: 'rgba(110, 110, 116, 0.6)',
    shadow3: 'rgba(78, 78, 84, 0.4)',
    inset: 'rgba(200, 200, 206, 0.35)',
  },
  rare: {
    inner: 'rgba(70, 170, 255, 0.98)',
    mid: 'rgba(10, 110, 255, 0.92)',
    outer: 'rgba(0, 70, 230, 0.72)',
    shadow1: 'rgba(30, 130, 255, 0.95)',
    shadow2: 'rgba(0, 85, 245, 0.75)',
    shadow3: 'rgba(0, 50, 210, 0.5)',
    inset: 'rgba(120, 195, 255, 0.4)',
  },
  epic: {
    inner: 'rgba(255, 120, 195, 0.98)',
    mid: 'rgba(255, 45, 155, 0.92)',
    outer: 'rgba(225, 10, 125, 0.72)',
    shadow1: 'rgba(255, 70, 175, 0.95)',
    shadow2: 'rgba(240, 20, 140, 0.75)',
    shadow3: 'rgba(200, 0, 110, 0.5)',
    inset: 'rgba(255, 165, 215, 0.4)',
  },
  legendary: {
    inner: 'rgba(255, 242, 50, 0.98)',
    mid: 'rgba(255, 220, 0, 0.95)',
    outer: 'rgba(245, 195, 0, 0.75)',
    shadow1: 'rgba(255, 232, 20, 0.98)',
    shadow2: 'rgba(255, 210, 0, 0.8)',
    shadow3: 'rgba(230, 175, 0, 0.5)',
    inset: 'rgba(255, 248, 120, 0.45)',
  },
};

/**
 * Rarity-colored floating bubbles overlay for order cards (mulligan, opened rewards).
 * Renders on top of the card; use inside a position: relative container that wraps the order.
 */
export function OrderCardBubbles({ rarity = 'common', className = '' }) {
  const configs = useMemo(() => {
    return Array.from({ length: BUBBLE_COUNT }, () => {
      const r = Math.random();
      const H = CARD_HEIGHT_PX;
      let endYPx;
      if (r < 0.2) {
        endYPx = -(H * 0.07 + Math.random() * H * 0.18);
      } else if (r < 0.4) {
        endYPx = -(H * 0.25 + Math.random() * H * 0.25);
      } else if (r < 0.6) {
        endYPx = -(H * 0.5 + Math.random() * H * 0.25);
      } else {
        endYPx = -(H * 0.75 + Math.random() * H * 0.25);
      }
      return {
        left: 5 + Math.random() * 87,
        bottom: Math.random() * 3,
        size: Math.round(SIZE_MIN + Math.random() * (SIZE_MAX - SIZE_MIN)),
        delay: -(Math.random() * 3),
        duration: 0.4 + Math.random() * 0.85,
        endYPx,
      };
    });
  }, []);

  const r = (rarity || 'common').toString().toLowerCase();
  const colors = RARITY_BUBBLE_COLORS[r] || RARITY_BUBBLE_COLORS.common;

  return (
    <div
      className={`order-card-bubbles ${className}`.trim()}
      aria-hidden="true"
      style={{
        ['--bubble-inner']: colors.inner,
        ['--bubble-mid']: colors.mid,
        ['--bubble-outer']: colors.outer,
        ['--bubble-shadow1']: colors.shadow1,
        ['--bubble-shadow2']: colors.shadow2,
        ['--bubble-shadow3']: colors.shadow3,
        ['--bubble-inset']: colors.inset,
      }}
    >
      {configs.map((cfg, i) => (
        <span
          key={i}
          className="order-card-bubble"
          style={{
            left: `${cfg.left}%`,
            bottom: `${cfg.bottom}%`,
            width: cfg.size,
            height: cfg.size,
            animationDelay: `${cfg.delay}s`,
            animationDuration: `${cfg.duration}s`,
            ['--bubble-end-px']: `${cfg.endYPx}px`,
          }}
        />
      ))}
    </div>
  );
}

export default OrderCardBubbles;
